import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { sharedApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Loader2, 
  CheckCircle2, 
  XCircle, 
  Clock,
  Network,
  User,
  FileText,
  Eye,
  DollarSign,
  Hash
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

const StatusBadge = ({ status }) => {
  const config = {
    pending: { color: 'bg-muted text-muted-foreground', icon: Clock },
    running: { color: 'bg-warning/20 text-warning', icon: Loader2 },
    completed: { color: 'bg-success/20 text-success', icon: CheckCircle2 },
    failed: { color: 'bg-destructive/20 text-destructive', icon: XCircle },
  };
  
  const { color, icon: Icon } = config[status] || config.pending;
  
  return (
    <Badge className={`${color} gap-1 font-code text-xs uppercase tracking-wider`}>
      <Icon className="w-3 h-3" />
      {status}
    </Badge>
  );
};

export default function SharedProjectPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedExpert, setSelectedExpert] = useState(null);

  useEffect(() => {
    const loadShared = async () => {
      try {
        const response = await sharedApi.get(token);
        setData(response.data);
        if (response.data.experts?.length > 0) {
          setSelectedExpert(response.data.experts[0]);
        }
      } catch (error) {
        setError('Project not found or link expired');
      } finally {
        setLoading(false);
      }
    };
    loadShared();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="bg-card border-border max-w-md w-full mx-4">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <XCircle className="w-12 h-12 text-destructive mb-4" />
            <p className="text-foreground text-center font-medium">{error}</p>
            <Link to="/login" className="text-primary hover:underline mt-4">
              Go to login
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { project, experts } = data;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-border">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center">
              <Network className="w-5 h-5 text-primary" />
            </div>
            <span className="font-heading font-semibold text-lg tracking-tight">
              LLM Council
            </span>
          </div>
          <Badge className="bg-muted text-muted-foreground gap-1">
            <Eye className="w-3 h-3" />
            Read-only
          </Badge>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Project Info */}
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-heading font-bold tracking-tight">Shared Project</h1>
          <Badge className="bg-primary/20 text-primary font-code">{project.phase}</Badge>
          {project.version > 1 && (
            <Badge variant="outline" className="font-code">v{project.version}</Badge>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center gap-6 text-sm text-muted-foreground">
          <span className="font-code">{new Date(project.created_at).toLocaleString()}</span>
          <span className="flex items-center gap-1">
            <DollarSign className="w-4 h-4" />${project.total_cost?.toFixed(4)}
          </span>
          <span className="flex items-center gap-1">
            <Hash className="w-4 h-4" />{project.total_tokens} tokens
          </span>
        </div>

        {/* Prompt Card */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Original Prompt
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-foreground whitespace-pre-wrap">{project.prompt}</p>
          </CardContent>
        </Card>

        {/* Content Tabs */}
        <Tabs defaultValue="synthesis" className="space-y-4">
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="synthesis">Final Synthesis</TabsTrigger>
            <TabsTrigger value="experts">Experts ({experts.length})</TabsTrigger>
          </TabsList>

          {/* Synthesis Tab */}
          <TabsContent value="synthesis">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading">Final Synthesis</CardTitle>
              </CardHeader>
              <CardContent>
                {project.final_synthesis ? (
                  <ScrollArea className="h-[500px]">
                    <div className="prose prose-invert prose-sm max-w-none">
                      <ReactMarkdown
                        components={{
                          code({node, inline, className, children, ...props}) {
                            const match = /language-(\w+)/.exec(className || '');
                            return !inline && match ? (
                              <SyntaxHighlighter style={oneDark} language={match[1]} PreTag="div" {...props}>
                                {String(children).replace(/\n$/, '')}
                              </SyntaxHighlighter>
                            ) : (
                              <code className={className} {...props}>{children}</code>
                            );
                          }
                        }}
                      >
                        {project.final_synthesis}
                      </ReactMarkdown>
                    </div>
                  </ScrollArea>
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    No synthesis available.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Experts Tab */}
          <TabsContent value="experts" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Expert List */}
              <div className="space-y-2">
                {experts.map((expert) => (
                  <Card 
                    key={expert.id}
                    className={`bg-card border-border cursor-pointer transition-all ${
                      selectedExpert?.id === expert.id ? 'border-primary ring-1 ring-primary/30' : 'hover:border-muted-foreground/50'
                    }`}
                    onClick={() => setSelectedExpert(expert)}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm">{expert.role}</span>
                        <StatusBadge status={expert.status} />
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span className="truncate">{expert.llm_model.split('/').pop()}</span>
                        <span>${expert.cost?.toFixed(3)}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Expert Detail */}
              <div className="lg:col-span-2">
                {selectedExpert ? (
                  <Card className="bg-card border-border h-full">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <User className="w-5 h-5 text-muted-foreground" />
                          <CardTitle className="font-heading">{selectedExpert.role}</CardTitle>
                        </div>
                        <StatusBadge status={selectedExpert.status} />
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="font-code">{selectedExpert.llm_model}</span>
                        <span>${selectedExpert.cost?.toFixed(4)}</span>
                        <span>{selectedExpert.tokens} tokens</span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {selectedExpert.response ? (
                        <ScrollArea className="h-80 rounded-md border border-border bg-background p-3">
                          <div className="prose prose-invert prose-sm max-w-none">
                            <ReactMarkdown
                              components={{
                                code({node, inline, className, children, ...props}) {
                                  const match = /language-(\w+)/.exec(className || '');
                                  return !inline && match ? (
                                    <SyntaxHighlighter style={oneDark} language={match[1]} PreTag="div" {...props}>
                                      {String(children).replace(/\n$/, '')}
                                    </SyntaxHighlighter>
                                  ) : (
                                    <code className={className} {...props}>{children}</code>
                                  );
                                }
                              }}
                            >
                              {selectedExpert.response}
                            </ReactMarkdown>
                          </div>
                        </ScrollArea>
                      ) : (
                        <p className="text-muted-foreground text-center py-8">No response</p>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="bg-card border-border h-full flex items-center justify-center">
                    <p className="text-muted-foreground">Select an expert to view details</p>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
